import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { TILE_PX, deriveDigest, encodeTile } from '../utils/screenWatermark';
import { inkForGround, measureGroundLuminance } from '../utils/screenGround';
import { paintScreenMarkTile } from '../utils/screenMarkPaint';

/**
 * Who was looking, written across the screen too faintly to read and too often to crop out.
 *
 * The IP watermark next to this one says which connection; this says which account, which is where
 * an investigation actually starts. It is deliberately a separate layer: the two answer different
 * questions, survive differently, and one being absent should not take the other with it.
 *
 * The payload is a truncated digest of the viewer's Tesseract subject, so the pixels name nobody
 * even to someone who works out what they are looking at. See `utils/screenWatermark.ts` for the
 * tile format and why it is not a QR code.
 * Security accessibility mode changes the dots to continuous horizontal lines while retaining
 * the same identity, tile repetition and enforcement. Ink and geometry live in screenMarkPaint.ts.
 *
 * Two things here are not about the format at all but about surviving the browser the mark is drawn
 * in. The polarity is measured off the page rather than taken from the theme
 * (`utils/screenGround.ts` has the measurements and the case that found it), and the layer's own
 * declarations are pinned inline at `!important` (`assertLayer` below). Both exist because an
 * extension can re-ground or blank this layer without touching the tile, and a mark that is present
 * but cancelled looks exactly like a mark that is working.
 */

/**
 * Under the IP watermark's ceiling and above everything else, so neither layer can be covered by a
 * modal and the two never fight over the same z-index.
 */
const MARK_Z_INDEX = 2147483646;

/** How long to let the page settle before re-measuring the ground, after something changed it. */
const GROUND_SETTLE_MS = 250;

/**
 * A late re-measure, for an agent that re-grounds the page without touching the nodes being watched.
 *
 * Dark Reader's dynamic theme took between one and two seconds to settle in testing, and it does
 * announce itself by appending stylesheets, so the observers below already catch it. This is for the
 * one that does not: it costs nine `elementsFromPoint` calls, once.
 */
const GROUND_LATE_MS = 2500;

/**
 * Every declaration the layer needs, so that all of them can be pinned in one place.
 *
 * These are set on the element imperatively rather than through a `style` prop or a class, and at
 * `!important`, which is the one priority a stylesheet cannot outrank — an inline important
 * declaration beats an important rule in any sheet, the page's own or an extension's. React is left
 * owning the node and nothing else about it, so the two never overwrite each other.
 *
 * `background-color` earns its place here on its own. Dark Reader's content script runs at
 * `document_start` and, while the real theme is still being computed, injects
 * `body :not(iframe) { background-color: #181a1b !important }`. That selector matches this layer,
 * which is `position: fixed`, `inset: 0` and near the top of the stacking order — so for as long as
 * the fallback is up, the entire panel is behind an opaque grey rectangle. The layer declared no
 * background colour of its own, so there was nothing to resist it.
 */
function declarationsFor(background: string): Array<[string, string]> {
  return [
    ['position', 'fixed'],
    ['inset', '0'],
    ['pointer-events', 'none'],
    ['overflow', 'hidden'],
    ['z-index', String(MARK_Z_INDEX)],
    ['display', 'block'],
    ['visibility', 'visible'],
    ['opacity', '1'],
    ['filter', 'none'],
    ['transform', 'none'],
    ['mix-blend-mode', 'normal'],
    ['background-color', 'transparent'],
    ['background-image', `url(${background})`],
    ['background-repeat', 'repeat'],
    ['background-position', '0 0'],
    ['background-attachment', 'scroll'],
    ['background-size', `${TILE_PX}px ${TILE_PX}px`],
  ];
}

/**
 * Tracks the luminance of the surface the mark is being painted on.
 *
 * Re-measured when the page is re-grounded, which in practice means when a stylesheet appears or
 * the root element's own styling changes. `null` until the first measurement lands, and after one
 * that could not be taken; the caller falls back to the theme for that case.
 */
function useGroundLuminance(theme: string): number | null {
  const [groundLuminance, setGroundLuminance] = useState<number | null>(null);

  useEffect(() => {
    let frame = 0;
    let settle = 0;
    const measure = () => setGroundLuminance(measureGroundLuminance());
    const schedule = () => {
      window.clearTimeout(settle);
      settle = window.setTimeout(measure, GROUND_SETTLE_MS);
    };

    // After a paint, so there is a laid-out page to sample rather than an empty root.
    frame = requestAnimationFrame(measure);
    const late = window.setTimeout(measure, GROUND_LATE_MS);

    // A stylesheet arriving is the signal that something may have re-grounded the page. Dark Reader
    // appends its sheets to a container in `body` and its own styles to `head`, so both are watched;
    // neither is observed with `subtree` beyond `head`, which keeps this off the app's hot paths.
    const heads = new MutationObserver(schedule);
    heads.observe(document.head, { childList: true, subtree: true });
    const bodies = new MutationObserver(schedule);
    bodies.observe(document.body, { childList: true });
    const root = new MutationObserver(schedule);
    root.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class', 'data-theme'] });

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      window.clearTimeout(late);
      heads.disconnect();
      bodies.disconnect();
      root.disconnect();
    };
  }, [theme]);

  return groundLuminance;
}

export const StaffScreenMark = () => {
  const { identity } = useAuth();
  const { theme } = useTheme();
  const { securityAccessibilityMode, isReady } = useUserPreferences();
  const [density, setDensity] = useState(() => (typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1));
  const layer = useRef<HTMLDivElement | null>(null);

  // Moving the window to a second monitor changes the density, and a tile painted for the old one
  // resamples into mush. Repaint instead.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    const onChange = () => setDensity(window.devicePixelRatio || 1);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [density]);

  const groundLuminance = useGroundLuminance(theme);

  /**
   * The Tesseract subject, which is the account Tesseract itself resolves an Argus user to.
   * The email is a fallback for an identity that predates it; without either there is nobody to
   * mark and the layer stays off rather than writing something meaningless.
   */
  const subject = identity?.raw?.custom?.sub || identity?.email || '';

  const background = useMemo(() => {
    if (!subject || !isReady) return null;
    // The theme is the fallback, not the answer: it says what the app intends, and the measurement
    // says what the browser is about to paint. They differ exactly when this matters.
    const ground = groundLuminance === null
      ? (theme === 'light' ? 'onLight' : 'onDark')
      : inkForGround(groundLuminance);
    try {
      return paintScreenMarkTile(encodeTile(deriveDigest(subject)), ground, securityAccessibilityMode ? 'horizontal' : 'dots', density);
    } catch {
      // A mark that cannot be built is not worth taking the page down for.
      return null;
    }
  }, [subject, theme, groundLuminance, density, securityAccessibilityMode, isReady]);

  /**
   * Holds the layer to its declarations, and puts it back if it is detached.
   *
   * This is not a defence against someone who means it — anything that can run a line of script in
   * this page can delete the node, and no amount of re-asserting changes that. It is a defence
   * against an extension that blanks the layer as a side effect of doing something else entirely,
   * which is the case that actually happens and the one where nobody finds out.
   */
  useLayoutEffect(() => {
    const element = layer.current;
    if (!element || !background) return;
    const declarations = declarationsFor(background);

    /**
     * Writes only what is actually wrong, which is what stops this chasing its own tail.
     *
     * Observer callbacks arrive as microtasks, after the write that caused them, so a re-entrancy
     * flag would already be clear by the time one ran and would guard nothing. Idempotence is the
     * real guard: a write triggers one more pass, that pass finds every declaration in place and
     * writes nothing, and it ends there. Bounded at one extra pass per external change.
     */
    const assert = () => {
      for (const [property, value] of declarations) {
        if (element.style.getPropertyValue(property) !== value || element.style.getPropertyPriority(property) !== 'important') {
          element.style.setProperty(property, value, 'important');
        }
      }
      if (!element.isConnected) document.body.appendChild(element);
    };

    // Pin the chosen bitmap before the content's first paint, including when switching modes.
    assert();
    const attributes = new MutationObserver(assert);
    attributes.observe(element, { attributes: true, attributeFilter: ['style', 'class'] });
    // Cheap: a boolean check per change to a direct child of `body`, which is where the app's own
    // modals and portals land.
    const detachment = new MutationObserver(() => { if (!element.isConnected) assert(); });
    detachment.observe(document.body, { childList: true });

    return () => {
      attributes.disconnect();
      detachment.disconnect();
    };
  }, [background]);

  if (!background) return null;

  // No className and no `style` prop: every declaration this layer depends on is set imperatively
  // above, at a priority nothing in a stylesheet can reach. Leaving half of it in a Tailwind class
  // would leave that half defeatable.
  return createPortal(<div ref={layer} aria-hidden="true" data-screen-mark="" />, document.body);
};
